import type { Address } from '~~/types/commercetools';

import { CheckoutClientType } from '~~/types/checkout/formData/checkoutClientType';

import { getVatNumberFromCustomerAddress } from '~~/helpers/getVatNumberFromCustomerAddress';
import registerAddressErrors from '~~/helpers/Checkout/registerAddressErrors/registerAddressErrors';
import type { ValidationProvider } from '~~/types/vee-validate';
import { useBilling, useUser } from '~/utils/ct';
import type { VatCompanyAddress } from '~~/types/vatValidation';
import { asyncDebounce } from '~~/helpers/lodash/asyncDebounce';
import {
  hasRegistryAddressToValidate,
  VAT_AUTOFILL_ADDRESS_FIELDS,
  isFieldUnanswered,
  getJudgedAddressKey,
} from '~~/helpers/vatValidation/getAddressMismatch';
import { ShippingAddressType } from '~~/types/checkout/ShippingAddressType';
import { ADDRESS_PERSISTENCE_DEBOUNCE_MS } from '~~/constants/checkout';
import { truncateAddressFields } from '~~/helpers/address/truncateAddressFields';
import { ADDRESS_TYPE } from '~~/types/AddressType';

const useCheckoutBilling = () => {

  const {
    billing: address,
    load: loadBilling,
  } = useBilling();
  const { load: loadUser } = useUser();
  const { clientType, setClientTypeFromAddress } = useCheckoutClientType('Billing');
  const { defaultAddress } = useCheckout();
  const {
    cart,
    error: cartError,
    setBillingDetails: setCartBillingDetails,
  } = useCartExtended();
  const { countryCode: country } = useLocalization();
  const {
    validateVatNumber,
    waitForPendingValidation,
    validateAddress,
    markAddressAsValidated,
    vatResult,
    companyAddress,
    addressMismatches,
    validatingAddressFields,
    shouldClearRegistryAddress,
    isBzstMarket,
    hasAddressMismatch,
    areVatDetailsValid,
    isAddressMatching,
    isValidationInProgress,
    hasStoredValidation,
  } = useVatValidation();
  const { isBillingAddressValidationEnabled, isVatValidationEnabled } = useStoreVsfExtended();

  const {
    isAddressAutofilled,
    hasAddressBeenAutofilled,
    resetAddressAutofill,
    setAddressAutofilled,
    dismissAutofillNotice,
  } = useVatAutofillAddress();

  const { billingDetails, setBillingDetailsMutation } = useBillingDetailsStore();
  const persistedBillingDetails = useState<Partial<Address>>('persistedBillingDetails', () => ({}));
  const { shippingAddressType } = useShippingAddressTypeStore();
  const { vatNumber, setVatNumber: setAddressVatNumber } = useCheckoutAddressVat('Billing');

  const isBzstValidationAllowed = computed(() =>
    isVatValidationEnabled.value &&
    isBillingAddressValidationEnabled.value &&
    isBzstMarket.value &&
    clientType.value === CheckoutClientType.COMPANY &&
    !!vatNumber.value,
  );

  const isBillingAddressCompleteForVatValidation = computed(() =>
    [
      billingDetails.value?.company,
      billingDetails.value?.streetName,
      billingDetails.value?.postalCode,
      billingDetails.value?.city,
    ].every(value => String(value ?? '').trim() !== ''),
  );


  const isBzstValidationCurrent = computed(() =>
    isBzstValidationAllowed.value &&
    areVatDetailsValid.value &&
    isAddressMatching.value &&
    !isValidationInProgress.value &&
    !cartError.value?.updateCustomCart &&
    hasStoredValidation(vatNumber.value, billingDetails.value),
  );

  const useCheckoutAddressInstance = useCheckoutAddress({
    addressType: 'Billing',
    scrollToTopOfForm: '#BillingForm',
    addressDetails: billingDetails,
  });

  const {
    displayNotification,
    displayWarningNotification,
    resetNotifications,
    scrollToField,
    setScrollToField,
    resetBusinessInfoForPrivateCustomer,
  } = useCheckoutAddressInstance;



  const setVatNumber = async (value: string) => {

    // Re-emitting the same number (the VAT input does it on every blur) leaves the autofill
    // that produced the current address intact - only a different number invalidates it.
    if (vatNumber.value === value) {

      return;

    }

    resetAddressAutofill();

    await setAddressVatNumber(value);

  };

  const areFieldValuesEqual = ({
    field,
    nextAddress,
    previousAddress,
  }: {
    field: keyof Address;
    nextAddress: Partial<Address>;
    previousAddress: Partial<Address>;
  }) => {

    return (nextAddress[field] ?? '') === (previousAddress[field] ?? '');

  };

  const persistBillingAddress = asyncDebounce(async (field?: string) => {

    if (!field || !cart.value) {

      return;

    }

    const typedField = field as keyof Address;

    if (areFieldValuesEqual({
      field: typedField,
      nextAddress: billingDetails.value,
      previousAddress: persistedBillingDetails.value,
    })) {

      return;

    }

    await waitForPendingValidation();

    const shippingAddress = shippingAddressType.value === ShippingAddressType.SAME_AS_BILLING
      ? truncateAddressFields({ ...billingDetails.value, id: undefined }, ADDRESS_TYPE.Shipping)
      : undefined;

    await setCartBillingDetails(billingDetails.value, {
      vatNumber: vatNumber.value,
      vatResult: vatResult.value,
      shippingAddress,
    });

    persistedBillingDetails.value = { ...billingDetails.value };

  }, ADDRESS_PERSISTENCE_DEBOUNCE_MS);

  const handleResetBusinessInfo = async () => {

    await resetBusinessInfoForPrivateCustomer({
      setVatNumber,
      clientType: clientType.value,
      setAddressDetailsMutation: setBillingDetailsMutation,
    });

  };

  const validateSelectedBillingVatDetails = async (forceValidate = false): Promise<void> => {

    const shouldValidateAddress = hasRegistryAddressToValidate(billingDetails.value);

    // The address always goes along, even when it is not to be judged against the registry -
    // the cart's billing address must track whatever address the validation status is being
    // recorded for, or the two end up out of sync (e.g. after switching to a fresh address).
    const hasValidated = await validateVatNumber({
      vatNumber: vatNumber.value,
      forceValidate,
      address: billingDetails.value,
    });

    // A stored result was already acted on when it landed; acting on it again would overwrite the
    // edits the user made since.
    if (!hasValidated) {

      return;

    }

    if (shouldClearRegistryAddress.value) {

      await clearVatAutofillAddress();

      return;

    }

    if (!shouldValidateAddress && companyAddress.value) {

      await applyVatAutofillAddress(companyAddress.value);

    }

  };

  /**
   * BzSt markets only judge the address together with the VAT number, so leaving the address page
   * always asks about the address as it stands - a stored answer is reused only while it still
   * speaks for that exact address.
   */
  const shouldValidateBeforeSubmit = (): boolean => {

    if (clientType.value !== CheckoutClientType.COMPANY) {

      return false;

    }

    return isBzstMarket.value || !vatResult.value;

  };

  const warnedAddressMismatchKey = useState<string | null>('useCheckoutBillingWarnedMismatchKey', () => null);

  /**
   * On BzSt markets a mismatching address holds the customer on the address page once, so the
   * field warnings are seen before they pay the standard rate. Leaving again with the same address
   * goes through - the warning was seen - while any changed address earns a warning of its own.
   */
  const holdForUnseenAddressMismatch = (): boolean => {

    if (clientType.value !== CheckoutClientType.COMPANY || !isBzstMarket.value || !hasAddressMismatch.value) {

      return false;

    }

    const addressKey = getJudgedAddressKey(billingDetails.value);

    if (addressKey === warnedAddressMismatchKey.value) {

      return false;

    }

    warnedAddressMismatchKey.value = addressKey;

    return true;

  };

  const isHeldForAddressMismatch = computed(() =>
    displayWarningNotification.value && isBzstMarket.value && hasAddressMismatch.value);

  const handleBillingBeforeSubmit = async (isValid: boolean,
    fields: Record<string, ValidationProvider> | undefined) => {

    resetNotifications();

    if (isValid && 'postalCode' in billingDetails.value) {

      await handleResetBusinessInfo();

      if (shouldValidateBeforeSubmit()) {

        await validateSelectedBillingVatDetails();

      }

      if (holdForUnseenAddressMismatch()) {

        displayNotification(true);

      }

    } else {

      registerAddressErrors(fields);

    }

  };

  const load = async () => {

    await loadUser();
    await loadBilling();

    await setBillingDetails(getInitialBillingAddress(address.value || defaultAddress));

  };

  const setBillingDetailsField = async (field: string, value: string) => {

    // Only the fields the autofill wrote can contradict the notice - the user still has to type
    // the ones it left empty, and doing so is no reason to retire it.
    if (VAT_AUTOFILL_ADDRESS_FIELDS.includes(field as keyof VatCompanyAddress)) {

      dismissAutofillNotice();

    }

    await setBillingDetails({
      ...billingDetails.value,
      [field]: value,
    }, {
      isFullAddress: false,
    });

  };

  const resetBillingDetailsForDifferentAddress = async () => {

    await setBillingDetails({ ...defaultAddress });

  };

  const handleBillingAddressBlur = async (field?: string) => {

    // BzSt markets validate the address only on request or when leaving the address page.
    if (isBillingAddressValidationEnabled.value && !isBzstMarket.value) {

      await validateAddress(billingDetails.value, field);

    }

    await persistBillingAddress(field);

  };

  /**
   * The registry answer as it should land in the form: the fields the registry never spoke for are
   * dropped rather than written as empty, so a postal code the customer already typed on a market
   * whose registry never states one survives the autofill instead of being wiped by it.
   */
  const getAutofillFields = (address: VatCompanyAddress): Partial<VatCompanyAddress> => {

    return Object.fromEntries(
      Object.entries(address)
        .filter(([field]) => !isFieldUnanswered(field as keyof VatCompanyAddress, address)),
    );

  };

  const applyVatAutofillAddress = async (address: VatCompanyAddress) => {

    await setBillingDetails({
      ...billingDetails.value,
      ...getAutofillFields(address),
    }, { isFullAddress: false, isVatAutofill: true });

    // The fields now hold the registry address, so the first blur on each of them has nothing
    // new to ask about.
    markAddressAsValidated(billingDetails.value);

  };

  /**
   * The mirror image of applyVatAutofillAddress: a validation that reached no verdict has nothing
   * to say about the registry fields, so they go back to empty. The fields the registry never
   * spoke for - the name, the phone - stay as they are.
   *
   * Only the "we filled this in for you" notice is retired, not the record that we ever did: the
   * hint offering the autofill is addressed to someone who has not entered a VAT number yet, and
   * bringing it back to someone who just did would read as a step backwards.
   */
  const clearVatAutofillAddress = async () => {

    const emptyRegistryFields = Object.fromEntries(
      VAT_AUTOFILL_ADDRESS_FIELDS.map(field => [field, '']),
    );

    await setBillingDetails({
      ...billingDetails.value,
      ...emptyRegistryFields,
    }, { isFullAddress: false });

    dismissAutofillNotice();

  };

  const setBillingDetails = async (
    address: Partial<Address>,
    {
      isFullAddress = true,
      isVatAutofill = false,
    }: {
      isFullAddress?: boolean;
      isVatAutofill?: boolean;
    } = {},
  ) => {

    const normalizedAddressCountry = address?.country?.toLowerCase();
    const normalizedCountry = country.value?.toLowerCase();

    if (normalizedAddressCountry && normalizedCountry && normalizedAddressCountry !== normalizedCountry) {

      await setCartBillingDetails({ country: country.value }, { vatNumber: '' });

      return;

    }

    const addressWithCountry = (!normalizedAddressCountry && country.value)
      ? { ...address, country: country.value }
      : { ...address };


    if (isFullAddress) {

      await setClientTypeFromAddress(addressWithCountry);

    }

    setBillingDetailsMutation(addressWithCountry);

    if (isFullAddress) {

      persistedBillingDetails.value = { ...addressWithCountry };

    }

    if (isVatAutofill) {

      setAddressAutofilled();

      return;

    }

    if (isFullAddress) {

      resetAddressAutofill();
      await setVatNumber(getVatNumberFromCustomerAddress(billingDetails.value));

      await validateSelectedBillingVatDetails();

    }

  };


  const getInitialBillingAddress = (address: Partial<Address>) => {

    if (!address.postalCode) {

      return defaultAddress;

    }

    return { ...address, id: undefined };

  };


  return {
    ...useCheckoutAddressInstance,
    load,
    vatNumber,
    setVatNumber,
    setBillingDetails,
    resetBillingDetailsForDifferentAddress,
    setBillingDetailsField,
    handleBillingAddressBlur,
    handleBillingBeforeSubmit,
    billingDetails,
    applyVatAutofillAddress,
    clearVatAutofillAddress,
    validateSelectedBillingVatDetails,
    isBzstValidationAllowed,
    isBzstValidationCurrent,
    isBillingAddressCompleteForVatValidation,
    isHeldForAddressMismatch,
    isAddressAutofilled,
    hasAddressBeenAutofilled,
    addressMismatches,
    validatingAddressFields,
    scrollToField,
    setScrollToField,
  };

};

export default useCheckoutBilling;
